import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { TID } from "../constants/testIds";
import { Bank, Star, FolderOpen, UserPlus, ArrowRight, Calculator } from "@phosphor-icons/react";

const StatCard = ({ label, value, sub, testId, icon: Icon, accent }) => (
  <div
    data-testid={testId}
    className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-sm transition-all"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      {Icon && (
        <div className={`h-8 w-8 rounded-md grid place-items-center ${accent || "bg-slate-100 text-slate-700"}`}>
          <Icon size={16} weight="duotone" />
        </div>
      )}
    </div>
    <div className="font-heading text-4xl font-bold tracking-tighter text-slate-900">{value ?? "—"}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentLenders, setRecentLenders] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/lenders").then(({ data }) => setRecentLenders((data || []).slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div data-testid={TID.dashboard} className="space-y-10">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Welcome back</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">
          {user?.name?.split(" ")[0] || "Operator"}.
        </h1>
        <p className="mt-2 text-slate-600 max-w-xl">
          Find the right lender, price deals in minutes, and keep the team's institutional knowledge in one place.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard label="Total Lenders" value={stats?.total_lenders} testId={TID.statTotalLenders} icon={Bank} />
        <StatCard label="Active" value={stats?.active_lenders} sub={`${stats?.preferred_lenders ?? 0} preferred`} testId={TID.statActiveLenders} icon={Star} accent="bg-blue-50 text-blue-600" />
        <StatCard label="Saved Deals" value={stats?.total_deals} testId={TID.statTotalDeals} icon={FolderOpen} />
        {user?.role === "admin" && (
          <StatCard label="Pending users" value={stats?.pending_users} testId={TID.statPending} icon={UserPlus} accent={stats?.pending_users ? "bg-amber-50 text-amber-700" : ""} />
        )}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/pricing" className="group bg-slate-900 text-white rounded-lg p-6 hover:bg-slate-800 transition-colors">
          <Calculator size={22} weight="duotone" />
          <div className="font-heading text-xl font-bold mt-3">Price a new deal</div>
          <p className="text-sm text-slate-300 mt-1">Upload bank statements and get ranked lender recommendations.</p>
          <div className="mt-4 inline-flex items-center text-sm font-medium">
            Start pricing <ArrowRight size={14} className="ml-1 transition-transform group-hover:translate-x-0.5" />
          </div>
        </Link>
        <Link to="/lenders" className="group bg-white border border-slate-200 rounded-lg p-6 hover:shadow-sm transition-all">
          <Bank size={22} weight="duotone" className="text-slate-700" />
          <div className="font-heading text-xl font-bold mt-3 text-slate-900">Browse lenders</div>
          <p className="text-sm text-slate-600 mt-1">Filter by industry, state, product, term, NSFs and more.</p>
          <div className="mt-4 inline-flex items-center text-sm font-medium text-blue-600">
            Open directory <ArrowRight size={14} className="ml-1 transition-transform group-hover:translate-x-0.5" />
          </div>
        </Link>
        <Link to="/deals" className="group bg-white border border-slate-200 rounded-lg p-6 hover:shadow-sm transition-all">
          <FolderOpen size={22} weight="duotone" className="text-slate-700" />
          <div className="font-heading text-xl font-bold mt-3 text-slate-900">Saved scenarios</div>
          <p className="text-sm text-slate-600 mt-1">Pick up where you left off on prior deal analyses.</p>
          <div className="mt-4 inline-flex items-center text-sm font-medium text-blue-600">
            Open list <ArrowRight size={14} className="ml-1 transition-transform group-hover:translate-x-0.5" />
          </div>
        </Link>
      </div>

      {/* Recent lenders */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Lenders</div>
            <div className="font-heading text-xl font-bold text-slate-900">Recently added</div>
          </div>
          <Link to="/lenders" className="text-sm text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.15em] text-slate-500">
            <tr>
              <th className="text-left px-6 py-3 font-semibold">Lender</th>
              <th className="text-left px-6 py-3 font-semibold">Products</th>
              <th className="text-left px-6 py-3 font-semibold">Funding range</th>
              <th className="text-left px-6 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {recentLenders.map((l) => (
              <tr key={l.id} className="border-t border-slate-200 hover:bg-slate-50">
                <td className="px-6 py-3">
                  <Link to={`/lenders/${l.id}`} className="font-semibold text-slate-900 hover:text-blue-600">
                    {l.name}
                  </Link>
                  <div className="text-xs text-slate-500">{l.contact_name}</div>
                </td>
                <td className="px-6 py-3 text-slate-600">{(l.product_types || []).join(", ")}</td>
                <td className="px-6 py-3 font-mono text-xs text-slate-700">
                  {l.min_funding != null ? `$${Number(l.min_funding).toLocaleString()}` : "—"} – {l.max_funding != null ? `$${Number(l.max_funding).toLocaleString()}` : "—"}
                </td>
                <td className="px-6 py-3">
                  <StatusBadge status={l.status} />
                </td>
              </tr>
            ))}
            {recentLenders.length === 0 && (
              <tr><td colSpan="4" className="px-6 py-10 text-center text-sm text-slate-500">No lenders yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    preferred: "bg-blue-50 text-blue-700 border-blue-200",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    inactive: "bg-slate-100 text-slate-600 border-slate-200",
    test: "bg-amber-50 text-amber-700 border-amber-200",
    hidden: "bg-slate-900 text-slate-300 border-slate-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-semibold rounded border ${map[status] || map.active}`}>
      {status || "active"}
    </span>
  );
}
